import { useMemo, useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { motion, AnimatePresence } from 'framer-motion';
import { useManuscript } from '@/context/manuscript-context';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Link, Scissors, Trash2, Undo2, Redo2, X, Pencil, Paintbrush, MessageSquare, ChevronDown, MoreHorizontal } from 'lucide-react';
import { SplitStyleSelector } from './split-style-selector';
import { StyleEditorDialog } from './style-editor-dialog';
import { ConnectionEditor } from './connection-editor';
import { ContextHero } from '@/components/context/ContextHero';
import markManuscriptImg from '@/assets/mark_manuscript_v6.png';


export function MarkingBottomSheet() {
    const {
        activeSelection,
        setActiveSelection,
        editingMarkId,
        setEditingMarkId,
        activeTab,
        setActiveTab,
        editingConnectionId,
        setEditingConnectionId,
        marks,
        addMark,
        deleteMark,
        updateMark,
        addConnection,
        updateConnection,
        deleteConnection,
        connections: allConnections,
        addDivider,
        deleteDivider,
        dividers,
        wordIndexMap,
        undo,
        redo,
        canUndo,
        canRedo,
        lastUsedStyleId,
        setLastUsedStyleId,
        drawModeStyleId,
        setDrawModeStyleId,
        markStyles,
        updateMarkStyle,
        addMarkStyle,
        deleteMarkStyle,
        ensureMarkStyle,
        manuallyOpened,
        setManuallyOpened,
        interactionConstraint
    } = useManuscript();

    const [noteText, setNoteText] = useState('');
    const [styleEditorOpen, setStyleEditorOpen] = useState(false);
    const [editingStyleId, setEditingStyleId] = useState<string | null>(null);
    const [showNote, setShowNote] = useState(false); // New state for note visibility
    const isDarkMode = useDarkMode();

    const editingMark = useMemo(() =>
        marks.find(m => m.id === editingMarkId),
        [marks, editingMarkId]);

    const currentRange = useMemo(() => {
        if (activeSelection) return activeSelection;
        if (editingMark) return { startWordId: editingMark.startWordId, endWordId: editingMark.endWordId };
        return null;
    }, [activeSelection, editingMark]);

    const existingMarks = useMemo(() => {
        if (!currentRange) return [];
        const sIdx = wordIndexMap.get(currentRange.startWordId);
        const eIdx = wordIndexMap.get(currentRange.endWordId);
        if (sIdx === undefined || eIdx === undefined) return [];

        const min = Math.min(sIdx, eIdx);
        const max = Math.max(sIdx, eIdx);

        return marks.filter(m => {
            const ms = wordIndexMap.get(m.startWordId);
            const me = wordIndexMap.get(m.endWordId);
            if (ms === undefined || me === undefined) return false;
            const mMin = Math.min(ms, me);
            const mMax = Math.max(ms, me);
            // Show any mark touching the selection
            return mMin <= max && mMax >= min;
        }).sort((a, b) => {
            // Prioritize marks that have EXACTLY the same range
            const aExact = a.startWordId === currentRange.startWordId && a.endWordId === currentRange.endWordId;
            const bExact = b.startWordId === currentRange.startWordId && b.endWordId === currentRange.endWordId;
            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;
            return (b.dateUpdated || 0) - (a.dateUpdated || 0);
        });
    }, [marks, currentRange, wordIndexMap]);

    // Sync note text when selection changes or editing mark changes
    useEffect(() => {
        // Priority: editingMark > existingMarks[0]
        if (editingMark?.annotation) {
            setNoteText(editingMark.annotation);
            setShowNote(true); // Show note if an editing mark has an annotation
        } else {
            const markWithNote = existingMarks.find(m => !!m.annotation);
            if (markWithNote?.annotation) {
                setNoteText(markWithNote.annotation);
                setShowNote(true); // Show note if an existing mark has an annotation
            } else {
                setNoteText('');
                // Don't hide note if user manually opened it
            }
        }
    }, [editingMark, existingMarks]);

    const isTabAllowed = (tab: string) => {
        if (!interactionConstraint?.onlyAllow) return true;

        // Always allow note and structure (div) tabs, they are globally useful
        if (tab === 'note' || tab === 'structure') return true;

        // Map 'mark' and 'highlight' to the same concept for flexibility
        if (tab === 'highlight') return interactionConstraint.onlyAllow.includes('highlight') || interactionConstraint.onlyAllow.includes('mark');
    };


    const existingConnections = useMemo(() => {
        if (editingConnectionId) {
            const conn = allConnections.find(c => c.id === editingConnectionId);
            return conn ? [conn] : [];
        }
        if (!activeSelection) return [];

        const sIdx = wordIndexMap.get(activeSelection.startWordId);
        const eIdx = wordIndexMap.get(activeSelection.endWordId);
        if (sIdx === undefined || eIdx === undefined) return [];

        const min = Math.min(sIdx, eIdx);
        const max = Math.max(sIdx, eIdx);

        return allConnections.filter(c => {
            const cs = wordIndexMap.get(c.startWordId);
            const ce = wordIndexMap.get(c.endWordId);
            if (cs === undefined || ce === undefined) return false;
            const cMin = Math.min(cs, ce);
            const cMax = Math.max(cs, ce);
            // Show any connection touching the selection
            return cMin <= max && cMax >= min;
        });
    }, [allConnections, activeSelection, wordIndexMap, editingConnectionId]);

    const existingDividers = useMemo(() => {
        const wordId = activeSelection?.startWordId || editingMark?.startWordId;
        if (!wordId) return [];

        // Find existing dividers for the currently focused word
        return dividers.filter(d => d.wordId === wordId);
    }, [dividers, activeSelection, editingMark]);

    const isOpen = !!activeSelection || !!editingMarkId || !!editingConnectionId || manuallyOpened;


    const handleClose = () => {
        setActiveSelection(null);
        setEditingMarkId(null);
        setEditingConnectionId(null);
        setManuallyOpened(false);
        setDrawModeStyleId(null);
        setShowNote(false); // Reset note visibility on close
        window.getSelection()?.removeAllRanges();
    };

    const handleAddHighlight = (styleId: string) => {
        if (!activeSelection) return;

        addMark({
            markStyleId: styleId,
            startWordId: activeSelection.startWordId,
            endWordId: activeSelection.endWordId,
        });
        if (!drawModeStyleId) handleClose();
    };

    // Get current style for split-button (from editing mark, draw mode, or default to Key Words)
    const currentStyle = useMemo(() => {
        const styleId = editingMark?.markStyleId || drawModeStyleId || lastUsedStyleId || 'yellow-pencil';
        return markStyles.find(s => s.id === styleId) || markStyles.find(s => s.id === 'yellow-pencil') || markStyles[0];
    }, [editingMark, drawModeStyleId, lastUsedStyleId, markStyles]);

    const handleStyleEditorSave = (updates: Partial<ManuscriptMarkStyle>) => {
        if (editingStyleId) {
            updateMarkStyle(editingStyleId, updates);
        } else {
            addMarkStyle(updates as Omit<ManuscriptMarkStyle, 'id'>);
        }
    };

    const handleStyleEditorDelete = () => {
        if (editingStyleId) {
            deleteMarkStyle(editingStyleId);
            setEditingStyleId(null);
            setStyleEditorOpen(false);
        }
    };

    const handleEditStyle = (styleId: string) => {
        setEditingStyleId(styleId);
        setStyleEditorOpen(true);
    };

    const handleNewStyle = () => {
        setEditingStyleId(null);
        setStyleEditorOpen(true);
    };

    const handleDelete = (markId: string) => {
        if (markId === editingMarkId) setEditingMarkId(null);
        deleteMark(markId);
        if (existingMarks.length <= 1) handleClose();
    };

    const handleSaveNote = useCallback((text: string) => {
        const targetMark = editingMark || existingMarks[0];

        if (targetMark) {
            // Only update if it actually changed
            if (targetMark.annotation !== text) {
                updateMark(targetMark.id, { annotation: text });
            }
        } else if (text.trim() && activeSelection) {
            addMark({
                startWordId: activeSelection.startWordId,
                endWordId: activeSelection.endWordId,
                annotation: text
            });
        }
    }, [activeSelection, editingMark, existingMarks, updateMark, addMark]);

    // Debounced autosave for notes
    useEffect(() => {
        const timer = setTimeout(() => {
            handleSaveNote(noteText);
        }, 1000); // 1 second debounce

        return () => clearTimeout(timer);
    }, [noteText, handleSaveNote]);

    const toggleDrawMode = (styleId: string) => {
        if (drawModeStyleId === styleId) {
            setDrawModeStyleId(null);
        } else {
            setDrawModeStyleId(styleId);
            setManuallyOpened(true);
        }
    };


    const handleUpdateConnectionLabel = (connId: string, label: string) => {
        updateConnection(connId, { label });
    };

    const handleToggleConnectionDirection = (connId: string, currentStyleId: string) => {
        updateConnection(connId, {
            connectionStyleId: currentStyleId === 'line' ? 'default-arrow' : 'line'
        });
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            // Check if we're actually clicking something that should keep us open
            const activeElement = document.activeElement;

            // Don't close if focus moved TO another element inside the drawer
            // (e.g., when switching tabs and a new element gets focus, or focus momentarily blurs)
            if (activeElement?.closest('[data-manuscript-tools]')) return;

            // Don't close if user is interacting with a range handle
            if (activeElement?.closest('.range-handle')) return;

            // Don't close if a handle is currently active (being dragged)
            if (document.querySelector('.range-handle:active')) return;

            // Don't close if focus moved to a Radix portal (like the dropdown menu)
            if (activeElement?.closest('[data-radix-portal]')) return;

            // Check if the click happened on a portal (for cases where focus hasn't moved yet)
            // or if we're currently in a state where the dropdown is likely open
            if (document.querySelector('[data-radix-portal]')) return;

            handleClose();
        }
    };

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className={cn(
                            "fixed bottom-0 left-0 right-0 z-[120] border-t shadow-[0_-20px_50px_rgba(0,0,0,0.3)] rounded-t-[2.5rem] overflow-hidden flex flex-col max-h-[90vh]",
                            isDarkMode
                                ? 'bg-zinc-950/80 backdrop-blur-3xl text-white border-white/10'
                                : 'bg-white/80 backdrop-blur-3xl text-black border-zinc-200'
                        )}
                        data-manuscript-tools
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ContextHero
                            eyebrow=""
                            headline=""
                            image={markManuscriptImg}
                            variant="tiny"
                            onClose={handleClose}
                            showHandle={false}
                            showClose={false}
                            className="rounded-b-none border-b-0 shrink-0"
                        >
                            <VisuallyHidden>
                                <h2>Manuscript Tools</h2>
                                <p>Apply highlights, connections, and notes to the biblical text.</p>
                            </VisuallyHidden>

                            <div className="relative flex items-center justify-between gap-2 md:gap-4 w-full -mt-6">
                                <h2 className={cn(
                                    "font-serif md:text-2xl font-medium shrink-0 ml-2",
                                    isDarkMode ? "text-white/90" : "text-white/95" // Stay white/light on hero image
                                )}>
                                    Manuscript Tools
                                </h2>

                                <div className="flex items-center gap-1.5 md:gap-2">
                                    <Button
                                        variant={drawModeStyleId ? 'default' : 'ghost'}
                                        size="sm"
                                        className={cn(
                                            "h-7 md:h-8 px-2 md:px-3 gap-1.5 rounded-full transition-all border border-white/20",
                                            drawModeStyleId
                                                ? "bg-white text-black border-transparent"
                                                : isDarkMode
                                                    ? "bg-transparent text-white/80 hover:bg-white/5"
                                                    : "bg-transparent text-white/90 hover:bg-black/10"
                                        )}
                                        onClick={() => toggleDrawMode(currentStyle.id)}
                                        title="Toggles between marking text highlight or drawing connections"
                                    >
                                        <Paintbrush className={cn("h-3 w-3 md:h-3.5 md:w-3.5", drawModeStyleId && "animate-pulse")} />
                                        <span className="font-bold uppercase tracking-wider text-[9px] md:text-[10px]">Draw</span>
                                    </Button>

                                    <DropdownMenu modal={false}>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className={cn(
                                                    "h-7 md:h-8 px-2 md:px-3 gap-1.5 rounded-full bg-transparent border transition-all",
                                                    isDarkMode
                                                        ? "text-white/80 border-white/20 hover:bg-white/5"
                                                        : "text-white/90 border-white/30 hover:bg-black/10"
                                                )}
                                            >
                                                <Plus className="h-3 w-3 md:h-3.5 md:w-3.5" />
                                                <span className="font-bold uppercase tracking-wider text-[9px] hidden md:block md:text-[10px]">Add</span>
                                                <ChevronDown className="h-3 w-3 md:h-3.5 md:w-3.5 opacity-50 hidden md:block" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                            align="end"
                                            className={cn(
                                                "w-48 backdrop-blur-xl border",
                                                isDarkMode
                                                    ? "bg-zinc-900/80 text-white border-white/10"
                                                    : "bg-white/80 text-black border-zinc-200"
                                            )}
                                        >
                                            <DropdownMenuItem
                                                className={cn(
                                                    "flex items-center gap-2 cursor-pointer py-2.5",
                                                    isDarkMode ? "focus:bg-white/10 focus:text-white" : "focus:bg-black/5 focus:text-black"
                                                )}
                                                onClick={() => setShowNote(!showNote)}
                                            >
                                                <MessageSquare className="h-4 w-4 opacity-70" />
                                                <span className="text-sm">Add Note</span>
                                            </DropdownMenuItem>

                                            <DropdownMenuItem
                                                className={cn(
                                                    "flex items-center gap-2 cursor-pointer py-2.5 disabled:opacity-30",
                                                    isDarkMode ? "focus:bg-white/10 focus:text-white" : "focus:bg-black/5 focus:text-black"
                                                )}
                                                disabled={!activeSelection}
                                                onClick={() => {
                                                    if (activeSelection) {
                                                        addConnection({
                                                            startWordId: activeSelection.startWordId,
                                                            endWordId: activeSelection.endWordId,
                                                            connectionStyleId: 'default-arrow',
                                                            label: ''
                                                        });
                                                    }
                                                }}
                                            >
                                                <Link className="h-4 w-4 opacity-70" />
                                                <span className="text-sm">Create Connection</span>
                                            </DropdownMenuItem>

                                            <div className={cn("h-px my-1", isDarkMode ? "bg-white/5" : "bg-black/5")} />

                                            <div className={cn("px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest", isDarkMode ? "text-white/30" : "text-black/30")}>Dividers</div>

                                            <DropdownMenuItem
                                                className={cn(
                                                    "flex items-center gap-2 cursor-pointer py-2",
                                                    isDarkMode ? "focus:bg-white/10 focus:text-white" : "focus:bg-black/5 focus:text-black"
                                                )}
                                                disabled={!activeSelection && !editingMark}
                                                onClick={() => {
                                                    const wordId = activeSelection?.startWordId || editingMark?.startWordId;
                                                    if (wordId) {
                                                        addDivider({ wordId, type: 'slash' });
                                                    }
                                                }}
                                            >
                                                <span className="font-mono text-xs w-4 text-center">/</span>
                                                <span className="text-sm">Single Divider</span>
                                            </DropdownMenuItem>

                                            <DropdownMenuItem
                                                className={cn(
                                                    "flex items-center gap-2 cursor-pointer py-2",
                                                    isDarkMode ? "focus:bg-white/10 focus:text-white" : "focus:bg-black/5 focus:text-black"
                                                )}
                                                disabled={!activeSelection && !editingMark}
                                                onClick={() => {
                                                    const wordId = activeSelection?.startWordId || editingMark?.startWordId;
                                                    if (wordId) {
                                                        addDivider({ wordId, type: 'double-slash' });
                                                    }
                                                }}
                                            >
                                                <span className="font-mono text-xs w-4 text-center">//</span>
                                                <span className="text-sm">Double Divider</span>
                                            </DropdownMenuItem>

                                            {existingDividers.length > 0 && (
                                                <>
                                                    <div className={cn("h-px my-1", isDarkMode ? "bg-white/5" : "bg-black/5")} />
                                                    <DropdownMenuItem
                                                        className="flex items-center gap-2 focus:bg-destructive/10 focus:text-destructive text-destructive/80 cursor-pointer py-2"
                                                        onClick={() => {
                                                            existingDividers.forEach(d => deleteDivider(d.id));
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                        <span className="text-sm">Remove Divider</span>
                                                    </DropdownMenuItem>
                                                </>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    <div className="flex items-center gap-0.5 md:gap-1 ml-1 md:ml-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={!canUndo}
                                            onClick={undo}
                                            className={cn(
                                                "h-7 w-7 md:h-8 md:w-8 rounded-full transition-all disabled:opacity-30",
                                                isDarkMode
                                                    ? "text-white/70 hover:text-white hover:bg-white/10"
                                                    : "text-white/80 hover:text-white hover:bg-black/10"
                                            )}
                                            title="Undo"
                                        >
                                            <Undo2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={!canRedo}
                                            onClick={redo}
                                            className={cn(
                                                "h-7 w-7 md:h-8 md:w-8 rounded-full transition-all disabled:opacity-30",
                                                isDarkMode
                                                    ? "text-white/70 hover:text-white hover:bg-white/10"
                                                    : "text-white/80 hover:text-white hover:bg-black/10"
                                            )}
                                            title="Redo"
                                        >
                                            <Redo2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                        </Button>
                                    </div>

                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleClose}
                                        className={cn(
                                            "h-7 w-7 md:h-8 md:w-8 rounded-full ml-2 transition-all",
                                            isDarkMode
                                                ? "bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
                                                : "bg-black/10 text-white/90 hover:bg-black/20 hover:text-white"
                                        )}
                                        title="Close Manuscript Tools"
                                    >
                                        <X className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                    </Button>
                                </div>
                            </div>
                        </ContextHero>

                        <div className="mx-auto w-full max-w-lg overflow-y-auto overflow-x-hidden pb-24">
                            <div className="p-4 pt-0 space-y-3">
                                {/* 1. Highlighting Section */}
                                <div className="space-y-2">
                                    {currentStyle && (
                                        <SplitStyleSelector
                                            currentStyle={currentStyle}
                                            onApply={(styleId) => {
                                                if (activeSelection) {
                                                    handleSaveNote(noteText);
                                                    handleAddHighlight(styleId);
                                                } else {
                                                    toggleDrawMode(styleId);
                                                }
                                            }}
                                        >
                                            <div className="p-2 space-y-3">
                                                {markStyles.length > 0 && (
                                                    <div className="space-y-1.5">
                                                        <div className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest px-2">Your Styles</div>
                                                        <div className="space-y-1">
                                                            {markStyles.slice(0, 7).map((style) => (
                                                                <div
                                                                    key={style.id}
                                                                    className={cn(
                                                                        "w-full flex items-center justify-between p-2 rounded-md group transition-colors",
                                                                        isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"
                                                                    )}
                                                                >
                                                                    <div
                                                                        className="flex items-center gap-2 flex-1 cursor-pointer"
                                                                        onClick={() => {
                                                                            if (editingMark) {
                                                                                updateMark(editingMark.id, { markStyleId: style.id });
                                                                            } else {
                                                                                handleAddHighlight(style.id);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className={`mark mark-texture-${style.texture} mark-${style.type} mark-is-start mark-is-end text-sm font-medium`}
                                                                            style={{
                                                                                '--mark-color': style.color.includes('rgba')
                                                                                    ? style.color
                                                                                    : style.color.replace('rgb', 'rgba').replace(')', ', 0.35)'),
                                                                                '--mark-thickness': style.thickness ? `${style.thickness}px` : '2px'
                                                                            } as any}
                                                                        >
                                                                            {style.name}
                                                                        </span>
                                                                    </div>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-6 w-6 opacity-100 md:opacity-0 md:group-hover:opacity-100"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEditStyle(style.id);
                                                                        }}
                                                                    >
                                                                        <Pencil className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    className="w-full h-9 text-xs font-semibold"
                                                    onClick={handleNewStyle}
                                                >
                                                    + New Custom Style
                                                </Button>
                                            </div>
                                        </SplitStyleSelector>
                                    )}
                                </div>

                                <AnimatePresence>
                                    {showNote && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="space-y-3 overflow-hidden"
                                        >
                                            <div className="relative group/note">
                                                <Textarea
                                                    placeholder="Add your note here..."
                                                    value={noteText}
                                                    autoFocus
                                                    onChange={(e) => setNoteText(e.target.value)}
                                                    className={cn(
                                                        "min-h-[80px] text-[15px] leading-relaxed resize-none shadow-sm rounded-2xl transition-all p-4 font-serif",
                                                        isDarkMode
                                                            ? "bg-white/5 border-white/10 focus-visible:ring-primary/20"
                                                            : "bg-black/5 border-black/10 focus-visible:ring-primary/20"
                                                    )}
                                                />
                                                {noteText && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="absolute top-2 right-2 h-7 w-7 rounded-full opacity-0 group-hover/note:opacity-100 transition-opacity bg-black/20 hover:bg-black/40 text-white"
                                                        onClick={() => {
                                                            setNoteText('');
                                                            handleSaveNote('');
                                                        }}
                                                        title="Clear Note"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <AnimatePresence mode="popLayout">
                                    {(existingMarks.length > 0 || existingConnections.length > 0 || existingDividers.length > 0) && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.95 }}
                                            className="space-y-3 pt-2"
                                        >
                                            <div className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest px-1">Applied to Selection</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {existingMarks.map(m => {
                                                    const style = markStyles.find(s => s.id === m.markStyleId);
                                                    return (
                                                        <motion.div
                                                            layout
                                                            key={m.id}
                                                            onClick={() => {
                                                                if (m.id === editingMarkId) {
                                                                    setEditingMarkId(null);
                                                                } else {
                                                                    setEditingMarkId(m.id);
                                                                    if (m.annotation) setShowNote(true);
                                                                }
                                                            }}
                                                            className={cn(
                                                                "flex items-center pl-2 pr-0.5 py-0.5 rounded-lg border group/elem cursor-pointer transition-all",
                                                                editingMarkId === m.id
                                                                    ? "ring-2 ring-primary/50"
                                                                    : "",
                                                                isDarkMode
                                                                    ? "bg-white/5 border-white/10 hover:bg-white/10"
                                                                    : "bg-black/5 border-black/10 hover:bg-black/10"
                                                            )}
                                                        >
                                                            <div className="flex items-center gap-2 mr-1">
                                                                <div
                                                                    className="w-2.5 h-2.5 rounded-full border border-black/10 shadow-sm"
                                                                    style={{ backgroundColor: style?.color || '#ccc' }}
                                                                />
                                                                {style?.name && (
                                                                    <span className="text-[11px] font-medium truncate max-w-[120px] text-muted-foreground/80">
                                                                        {style.name}
                                                                    </span>
                                                                )}
                                                                {m.annotation && (
                                                                    <div className="flex items-center gap-1.5 ml-1">
                                                                        <MessageSquare className={cn(
                                                                            "h-3 w-3",
                                                                            editingMarkId === m.id ? "text-primary" : "text-primary/40"
                                                                        )} />
                                                                        <span className="text-[11px] font-medium italic text-muted-foreground/60">
                                                                            Note attached
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {!style?.name && !m.annotation && (
                                                                    <span className="text-[11px] font-medium text-muted-foreground/80">
                                                                        Highlight
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 transition-all rounded-md"
                                                                onClick={() => handleDelete(m.id)}
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </Button>
                                                        </motion.div>
                                                    );
                                                })}
                                                {existingConnections.map(conn => (
                                                    <motion.div layout key={conn.id} className="w-full">
                                                        <ConnectionEditor
                                                            connection={conn}
                                                            onUpdateLabel={handleUpdateConnectionLabel}
                                                            onToggleDirection={handleToggleConnectionDirection}
                                                            onDelete={deleteConnection}
                                                        />
                                                    </motion.div>
                                                ))}
                                                {existingDividers.map(d => (
                                                    <motion.div
                                                        layout
                                                        key={d.id}
                                                        className={cn(
                                                            "flex items-center pl-2 pr-0.5 py-0.5 rounded-lg border group/elem",
                                                            isDarkMode
                                                                ? "bg-white/5 border-white/10"
                                                                : "bg-black/5 border-black/10"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-2 mr-1">
                                                            <Scissors className="h-3 w-3 text-muted-foreground/40" />
                                                            <span className="text-[11px] font-medium text-muted-foreground/80">
                                                                {d.type === 'slash' ? '/' : '//'}
                                                            </span>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6 text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 transition-all rounded-md"
                                                            onClick={() => deleteDivider(d.id)}
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </Button>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <StyleEditorDialog
                open={styleEditorOpen}
                onOpenChange={setStyleEditorOpen}
                style={editingStyleId ? markStyles.find(s => s.id === editingStyleId) : undefined}
                onSave={handleStyleEditorSave}
                onDelete={handleStyleEditorDelete}
            />
        </>
    );
}
